<script setup lang="ts">
/**
 * HLOS-222 — FeedbackButton (canal discreto para feedback da família).
 *
 * Constitucional:
 * - CL-08 (Ética fechada): zero PII — feedback é opcional e anônimo.
 * - Princípio X (Dignidade): botão discreto (não intrusivo, não spam).
 * - CC-06 (zero dark patterns): copy neutra, zero "Compartilhe obrigatoriamente".
 *
 * Skills HLOS governando:
 * - vue-shell: Composition API + scoped CSS + data-testid para Playwright
 * - companion-product: UX respeitosa (zero coercitiva)
 */

import { useFeedbackStore } from "./stores/feedback";

/**
 * Eventos emitidos:
 * - `feedback-open`: usuário clicou no botão (pai abre modal/form).
 *
 * Pai (HomeState) usa para abrir modal de feedback.
 */
interface Emits {
  (e: "feedback-open"): void;
}

const emit = defineEmits<Emits>();

function onClick(): void {
  emit("feedback-open");
}

// Referência ao store (mantém o componente integrado com o fluxo de opt-in).
// Usado em v0.2 quando modal abrir.
const _feedback = useFeedbackStore();
void _feedback;
</script>

<template>
  <button
    type="button"
    class="feedback-button"
    data-testid="feedback-button"
    aria-label="Compartilhar feedback"
    @click="onClick"
  >
    <span aria-hidden="true">💬</span>
    <span class="feedback-button__sr">Compartilhar feedback</span>
  </button>
</template>

<style scoped>
.feedback-button {
  /* CC-06: botão discreto (sem destaque dark). */
  font: inherit;
  padding: 0.4rem 0.6rem;
  border: 1px solid color-mix(in srgb, currentColor 20%, transparent);
  border-radius: 0.375rem;
  background: transparent;
  cursor: pointer;
  opacity: 0.7;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  transition: opacity 0.2s ease;
}
.feedback-button:hover {
  opacity: 1;
}
.feedback-button:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 2px;
  opacity: 1;
}
.feedback-button__sr {
  /* Screen reader label (sr-only). */
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
